import React, { useState, useEffect, useContext } from "react";
import { Outlet, NavLink } from "react-router-dom";
import EmpleadoSidebar from "../components/EmpleadoSidebar";
import { AuthContext } from "../context/AuthContext";
import { SubscriptionContext } from "../context/SubscriptionContext";
import { Menu, Fingerprint, User, AlertCircle, CalendarOff, RefreshCw, Lock } from "lucide-react";
import { usePushNotifications } from '../hooks/usePushNotifications';

const bottomItems = [
  { path: "/empleados/fichar",      label: "Fichar",      icon: <Fingerprint size={20} /> },
  { path: "/empleados/perfil",      label: "Perfil",      icon: <User size={20} /> },
  { path: "/empleados/incidencias", label: "Incidencias", icon: <AlertCircle size={20} /> },
  { path: "/empleados/ausencias",   label: "Ausencias",   icon: <CalendarOff size={20} /> },
];

/** Pantalla de bloqueo para empleados cuya empresa no tiene suscripción activa */
function SubscriptionExpiredScreen() {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", minHeight: "60vh", padding: "40px 24px",
      textAlign: "center",
    }}>
      <div style={{
        width: 72, height: 72, borderRadius: "50%",
        background: "rgba(245,158,11,.1)", display: "flex",
        alignItems: "center", justifyContent: "center", marginBottom: 24,
      }}>
        <Lock size={32} style={{ color: "#B45309" }} />
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--clr-text)", margin: "0 0 10px" }}>
        Acceso suspendido
      </h2>

      <p style={{ fontSize: 14, color: "var(--clr-muted)", maxWidth: 360, margin: "0 0 8px", lineHeight: 1.6 }}>
        Tu empresa no tiene una suscripción activa en este momento.
      </p>
      <p style={{ fontSize: 13, color: "var(--clr-muted)", maxWidth: 360, margin: 0, lineHeight: 1.6 }}>
        Contacta con el administrador de tu empresa para reactivar la cuenta.
      </p>
    </div>
  );
}

export default function EmpleadoLayout() {
  const { subscribe } = usePushNotifications();
  useEffect(() => { subscribe(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { subscribed, loading: loadingSub } = useContext(SubscriptionContext);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
  }, [sidebarOpen]);

  const initial = (user?.nombre || user?.rol || "E").charAt(0).toUpperCase();

  // Spinner mientras verificamos suscripción
  if (loadingSub) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 14, color: "var(--clr-muted)" }}>
        <RefreshCw size={28} style={{ animation: "spin 1s linear infinite", color: "var(--clr-accent)" }} />
        <span style={{ fontSize: 14 }}>Cargando…</span>
      </div>
    );
  }

  return (
    <div className="tn-shell">
      {sidebarOpen && (
        <div className="tn-overlay d-lg-none" onClick={() => setSidebarOpen(false)} />
      )}
      <EmpleadoSidebar
        onClose={() => setSidebarOpen(false)}
        subscribed={subscribed}
        isOpen={sidebarOpen}
      />

      <div className="tn-main" style={{ width: "100%" }}>
        <header className="tn-topbar">
          <button className="tn-topbar__hamburger d-lg-none" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú">
            <Menu size={22} />
          </button>

          <div className="d-flex align-items-center gap-2 d-lg-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" class="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
            <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--clr-text)", letterSpacing: "-0.3px" }}>Turnora</span>
          </div>

          <div className="ms-auto d-flex align-items-center gap-3">
            <span style={{ fontSize: 13, color: "var(--clr-muted)" }} className="d-none d-md-inline">
              {user?.rol === "empleado" ? "Empleado" : user?.rol}
            </span>
            <div className="tn-avatar">{initial}</div>
          </div>
        </header>

        <main className="tn-page">
          {/* Si no hay suscripción, reemplazar el contenido por la pantalla de bloqueo */}
          {subscribed === false ? <SubscriptionExpiredScreen /> : <Outlet />}
        </main>
      </div>

      {/* Bottom nav (mobile) — bloqueada sin suscripción */}
      <nav className="tn-bottom-nav">
        {subscribed ? (
          bottomItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `tn-bottom-nav__item ${isActive ? "active" : ""}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))
        ) : (
          <div className="tn-bottom-nav__item" style={{ opacity: 0.5, cursor: "not-allowed", flex: 1 }}>
            <Lock size={20} />
            <span>Bloqueado</span>
          </div>
        )}
      </nav>
    </div>
  );
}
